import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useApiClient } from '@rudi/api/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import RRWebPlayer from 'rrweb-player';
import 'rrweb-player/dist/style.css';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@rudi/ui/select';
import { ArrowLeft, ExternalLink, RefreshCw, Video } from 'lucide-react';
import { PageHeader, QueryState } from '../components/page';
import { useReplayRecording, useReplays, useTenants, useUsers } from '../api/hooks';
import type { AdminTenant, AdminUser, ReplayListParams, ReplaySessionResponse } from '../api/types';

type PlayerOptions = ConstructorParameters<typeof RRWebPlayer>[0];
type PlayerInstance = {
  $destroy?: () => void;
  getMetaData?: () => { startTime: number; endTime: number; totalTime: number };
  goto?: (timeOffset: number, play?: boolean) => void;
};

/**
 * `session` opens one recording. `from` and `to` (ISO datetimes) mark a window
 * inside it, such as the minutes before a Feedback ticket; the player starts at `from`.
 */
export interface ReplaysSearch {
  session?: string;
  from?: string;
  to?: string;
}

export function validateReplaysSearch(search: Record<string, unknown>): ReplaysSearch {
  const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined);
  const time = (value: unknown) => {
    const iso = text(value);
    return iso && !Number.isNaN(Date.parse(iso)) ? iso : undefined;
  };
  return { session: text(search.session), from: time(search.from), to: time(search.to) };
}

/** Milliseconds from the recording's first event to `startAt`, inside the recording. */
export function replayOffset(
  startAt: string,
  meta: { startTime: number; totalTime: number }
): number {
  const offset = Date.parse(startAt) - meta.startTime;
  if (Number.isNaN(offset)) return 0;
  return Math.min(Math.max(0, offset), Math.max(0, meta.totalTime));
}

/**
 * rrweb draws the page from a full snapshot (event type 2) and replays the
 * changes after it; without one the player shows an empty frame. Rudi dropped
 * the snapshot of a large page until its recorder learned to gzip them.
 */
export function hasPageSnapshot(events: unknown[]): boolean {
  return events.some(event => (event as { type?: unknown } | null)?.type === 2);
}

const ALL = 'all';
const PAGE_SIZE = 50;

function shortId(value: string): string {
  return value.length <= 10 ? value : `${value.slice(0, 8)}...`;
}

function formatDateTime(value: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(new Date(value));
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '-';
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

function formatPage(url: string | null): string {
  if (!url) return '-';
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}` || parsed.hostname;
  } catch {
    return url;
  }
}

function formatKind(kind: string): string {
  if (kind === 'qa_review') return 'QA review';
  if (kind === 'dev_review') return 'Dev review';
  if (kind === 'chat' || kind === 'app') return 'App';
  return kind;
}

function tenantLabel(tenants: AdminTenant[], tenantId: string): string {
  return tenants.find(t => t.id === tenantId)?.name ?? shortId(tenantId);
}

function userLabel(users: AdminUser[], userId: string): string {
  const user = users.find(u => u.id === userId);
  if (!user) return shortId(userId);
  return user.email ?? user.display_name ?? user.username;
}

function userDetail(users: AdminUser[], userId: string): string | null {
  const user = users.find(u => u.id === userId);
  if (!user) return null;
  return user.tenant_name ?? user.username;
}

function statusBadgeVariant(status: string): 'default' | 'secondary' | 'outline' {
  if (status === 'closed') return 'default';
  if (status === 'open') return 'secondary';
  return 'outline';
}

function ReplayPlayer({ session, startAt }: { session: ReplaySessionResponse; startAt?: string }) {
  const recording = useReplayRecording(session.id);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(1024);

  const events = useMemo(() => recording.data ?? [], [recording.data]);
  const playable = useMemo(() => hasPageSnapshot(events), [events]);
  const height = Math.max(420, Math.round(width * 0.56));

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const updateWidth = () => {
      setWidth(Math.max(720, Math.floor(host.clientWidth)));
    };
    updateWidth();

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        setWidth(Math.max(720, Math.floor(entry.contentRect.width)));
      }
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const target = targetRef.current;
    if (!target || !playable) return;

    target.replaceChildren();
    const props: PlayerOptions['props'] = {
      events: events as PlayerOptions['props']['events'],
      width,
      height,
      autoPlay: false,
      showController: true,
      speedOption: [1, 2, 4, 8],
      maxScale: 1,
    };
    const player = new RRWebPlayer({ target, props }) as PlayerInstance;
    if (startAt && player.getMetaData && player.goto) {
      player.goto(replayOffset(startAt, player.getMetaData()), false);
    }
    return () => {
      player.$destroy?.();
      target.replaceChildren();
    };
  }, [events, height, playable, startAt, width]);

  if (recording.isLoading) {
    return (
      <div className="rounded-lg border py-16 text-center text-sm text-muted-foreground">
        Loading recording...
      </div>
    );
  }

  if (recording.error) {
    return (
      <div className="rounded-lg border py-16 text-center text-sm text-destructive">
        {recording.error instanceof Error ? recording.error.message : 'Failed to load recording'}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="rounded-lg border py-16 text-center text-sm text-muted-foreground">
        This session has no replay events.
      </div>
    );
  }

  if (!playable) {
    return (
      <div className="rounded-lg border py-16 text-center text-sm text-muted-foreground">
        This recording has no snapshot of the page, so there is nothing to play. Rudi used to drop
        the snapshot of a page too large to upload.
      </div>
    );
  }

  return (
    <div ref={hostRef} className="overflow-auto rounded-lg border bg-background">
      <div ref={targetRef} className="min-h-[520px]" />
    </div>
  );
}

function ReplayDetail({
  session,
  tenants,
  users,
  ticketWindow: replayWindow,
  onBack,
}: {
  session: ReplaySessionResponse;
  tenants: AdminTenant[];
  users: AdminUser[];
  ticketWindow?: { from?: string; to?: string };
  onBack: () => void;
}) {
  const metadataEntries = Object.entries(session.metadata ?? {}).filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  );

  return (
    <div className="mb-6 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Video className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold tracking-tight">
              {userLabel(users, session.user_id)}
            </h2>
            <Badge variant={statusBadgeVariant(session.status)}>{session.status}</Badge>
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            {tenantLabel(tenants, session.tenant_id)} · {formatDateTime(session.started_at)}
          </div>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      <div className="grid gap-3 text-sm md:grid-cols-4">
        <div className="rounded-md border px-3 py-2">
          <div className="text-xs text-muted-foreground">Page</div>
          <div className="mt-1 truncate font-medium" title={session.page_url ?? undefined}>
            {formatPage(session.page_url)}
          </div>
        </div>
        <div className="rounded-md border px-3 py-2">
          <div className="text-xs text-muted-foreground">Kind</div>
          <div className="mt-1 font-medium">{formatKind(session.kind)}</div>
        </div>
        <div className="rounded-md border px-3 py-2">
          <div className="text-xs text-muted-foreground">Duration</div>
          <div className="mt-1 font-medium">{formatDuration(session.duration_ms)}</div>
        </div>
        <div className="rounded-md border px-3 py-2">
          <div className="text-xs text-muted-foreground">Events</div>
          <div className="mt-1 font-medium">
            {session.events_count.toLocaleString()} in {session.chunk_count} chunks
          </div>
        </div>
      </div>

      {session.page_url && (
        <a
          href={session.page_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open recorded page
        </a>
      )}

      {replayWindow?.from && (
        <div className="rounded-md border px-3 py-2 text-sm">
          <span className="font-medium">Ticket window:</span> {formatTime(replayWindow.from)}
          {replayWindow.to && <> to {formatTime(replayWindow.to)}</>}
          <span className="text-muted-foreground"> · the player starts at the window</span>
        </div>
      )}

      {metadataEntries.length > 0 && (
        <div className="rounded-md border px-3 py-2 text-xs text-muted-foreground">
          {metadataEntries.map(([key, value]) => (
            <span key={key} className="mr-4 inline-block">
              <span className="font-medium text-foreground">{key}:</span>{' '}
              {typeof value === 'string' ? value : JSON.stringify(value)}
            </span>
          ))}
        </div>
      )}

      <ReplayPlayer session={session} startAt={replayWindow?.from} />
    </div>
  );
}

export function ReplaysRoutePage() {
  const search = useSearch({ from: '/replays' });
  return <ReplaysPage search={search} />;
}

export function ReplaysPage({ search = {} }: { search?: ReplaysSearch }) {
  const [tenantId, setTenantId] = useState(ALL);
  const [userId, setUserId] = useState(ALL);
  const [kind, setKind] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [selectedId, setSelectedId] = useState<string | null>(search.session ?? null);
  const api = useApiClient();
  const selectedReplay = useQuery({
    queryKey: ['replay-session', selectedId],
    queryFn: async () =>
      (await api.get<ReplaySessionResponse>(`/cortex/replays/${selectedId}`)).data,
    enabled: !!selectedId,
  });

  const tenants = useTenants();
  const users = useUsers();

  const params = useMemo<ReplayListParams>(
    () => ({
      tenant_id: tenantId === ALL ? undefined : tenantId,
      user_id: userId === ALL ? undefined : userId,
      kind: kind === ALL ? undefined : kind,
      status: status === ALL ? undefined : status,
      limit: PAGE_SIZE,
      offset: 0,
    }),
    [kind, status, tenantId, userId]
  );

  const replays = useReplays(params);
  const tenantList = useMemo(() => tenants.data?.tenants ?? [], [tenants.data?.tenants]);
  const userList = useMemo(() => users.data?.users ?? [], [users.data?.users]);
  const sessions = useMemo(() => replays.data?.sessions ?? [], [replays.data?.sessions]);
  const selected =
    selectedReplay.data ?? sessions.find(session => session.id === selectedId) ?? null;

  const selectableUsers = useMemo(() => {
    if (tenantId === ALL) return userList;
    return userList.filter(user => user.tenant_id === tenantId);
  }, [tenantId, userList]);

  useEffect(() => {
    if (userId === ALL || tenantId === ALL) return;
    if (!selectableUsers.some(user => user.id === userId)) {
      setUserId(ALL);
    }
  }, [selectableUsers, tenantId, userId]);

  return (
    <div>
      <PageHeader
        title="Replays"
        description="rrweb recordings captured from users who have session replay enabled."
        actions={
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={replays.isFetching}
            onClick={() => void replays.refetch()}
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <Select
          value={tenantId}
          onValueChange={value => {
            setTenantId(value);
            setSelectedId(null);
          }}
        >
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Tenant" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All tenants</SelectItem>
            {tenantList.map(tenant => (
              <SelectItem key={tenant.id} value={tenant.id}>
                {tenant.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={userId}
          onValueChange={value => {
            setUserId(value);
            setSelectedId(null);
          }}
        >
          <SelectTrigger className="w-64">
            <SelectValue placeholder="User" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All users</SelectItem>
            {selectableUsers.slice(0, 150).map(user => (
              <SelectItem key={user.id} value={user.id}>
                {user.email ?? user.display_name ?? user.username}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={kind}
          onValueChange={value => {
            setKind(value);
            setSelectedId(null);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Kind" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All kinds</SelectItem>
            <SelectItem value="chat">App</SelectItem>
            <SelectItem value="qa_review">QA review</SelectItem>
            <SelectItem value="dev_review">Dev review</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={status}
          onValueChange={value => {
            setStatus(value);
            setSelectedId(null);
          }}
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selected && (
        <ReplayDetail
          session={selected}
          tenants={tenantList}
          users={userList}
          ticketWindow={selected.id === search.session ? search : undefined}
          onBack={() => setSelectedId(null)}
        />
      )}

      <QueryState isLoading={replays.isLoading} error={replays.error} empty={sessions.length === 0}>
        <div className="mb-2 text-xs text-muted-foreground">
          Showing {sessions.length} of {replays.data?.total ?? sessions.length} sessions.
        </div>
        <div className="rounded-3xl border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Page</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Events</TableHead>
                <TableHead>Started</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.map(session => (
                <TableRow
                  key={session.id}
                  className="cursor-pointer"
                  data-state={session.id === selectedId ? 'selected' : undefined}
                  onClick={() => setSelectedId(session.id)}
                >
                  <TableCell>
                    <div className="font-medium">{userLabel(userList, session.user_id)}</div>
                    {userDetail(userList, session.user_id) && (
                      <div className="text-xs text-muted-foreground">
                        {userDetail(userList, session.user_id)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {tenantLabel(tenantList, session.tenant_id)}
                  </TableCell>
                  <TableCell className="max-w-56 truncate text-muted-foreground">
                    {formatPage(session.page_url)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{formatKind(session.kind)}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusBadgeVariant(session.status)}>{session.status}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDuration(session.duration_ms)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {session.events_count.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(session.started_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </QueryState>
    </div>
  );
}
